import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DataType, ExprCall } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { AccountFacade } from '@mode/accounts/contract';
import { TooltipSize, capraIconFieldCalcNumeric, capraIconMeasureNames, capraIconMeasureValues } from '@mode/capra';
import { ColorsModalFacade, FieldFormatModalService } from '@mode/charts/contract';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { getFieldIcon, iconDataFromColumnType } from '@mode/shared/util-js';
import { CompiledAttr, Switch } from '@mode/shared/util-switch';
import { ContextMenuComponent, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { Subject, take, takeUntil } from 'rxjs';
import { DragData } from '../field-list/field-list.component';

const genericErrorHTML = `This calculated field is broken.`;

const localNameErrorHtml = `This field name conflicts with a column<br/>
                            or field in the underlying dataset. Edit the<br/>
                            local field name to resolve the conflict.`;

const parentNameErrorHtml = `This field name conflicts with a local<br/>
                            field in the Exploration. Edit the local<br/>
                            field name to resolve the conflict.`;

const formulaErrorHtml = `The formula used in this field is no<br/>
                         longer valid. Edit the existing formula<br/>
                         to see what’s wrong, or delete the field.`;

@Component({
  selector: 'mode-field-list-attr',
  templateUrl: './field-list-attr.component.html',
  styleUrls: ['./field-list-attr.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldListAttrComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() readOnly = false;
  @Input() attr!: CompiledAttr;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() chartObjectType: ChartTypes.ObjectType = ChartTypes.ObjectType.Visualization;
  @Input() hasLocalCalcNameError = false;
  @Input() hasParentCalcNameError = false;
  @Input() isFromImportedDataset = false;
  @Input() queryRunToken = '';

  @Output() dragStart = new EventEmitter<DragData>();
  @Output() dragEnd = new EventEmitter();
  @Output() editCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() viewCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() removeCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() duplicateCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() applyFieldColor = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    color: ChartTypes.FieldColor;
  }>();
  @Output() applyFieldFormat = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    format: ChartTypes.TextFormat;
  }>();
  @Output() applyDefaultAgg = new EventEmitter<{
    attr: FlamingoTypes.ModeAttr;
    defaultAgg: ExprCall.Name;
  }>();

  @ViewChild('attrContainer') attrContainer!: ElementRef;

  private destroyed$ = new Subject<void>();
  hasContextMenu = false;
  canModifyAttr = false;
  hideFormatMenuOption = false;
  calcAttr: FlamingoTypes.ModeCalc | null = null;
  defaultAgg: ExprCall.Name | undefined;
  dimensionAggOptions = [
    { value: ExprCall.Name.Count, label: 'Count' },
    { value: ExprCall.Name.CountD, label: 'Count (Distinct)' },
    { value: ExprCall.Name.CountDA, label: 'Approx. Count (Distinct)' },
  ];
  measureAggOptions = [
    { value: ExprCall.Name.Sum, label: 'Sum' },
    { value: ExprCall.Name.Count, label: 'Count' },
    { value: ExprCall.Name.CountD, label: 'Count (Distinct)' },
    { value: ExprCall.Name.CountDA, label: 'Approx. Count (Distinct)' },
    { value: ExprCall.Name.Avg, label: 'Average' },
    { value: ExprCall.Name.Max, label: 'Maximum' },
    { value: ExprCall.Name.Min, label: 'Minimum' },
  ];
  @ViewChild(ContextMenuComponent) menu!: ContextMenuComponent;

  constructor(
    public contextMenuService: ContextMenuService,
    private changeDetection: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private formatModalService: FieldFormatModalService,
    private colorsModalFacade: ColorsModalFacade,
    private accountFacade: AccountFacade
  ) {}

  ngAfterViewInit(): void {
    this.attrContainer?.nativeElement.addEventListener('dragstart', (evt: any) => {
      if (evt.dataTransfer) {
        evt.dataTransfer.setData('text/plain', evt.target.id);
      }
    });
    this.changeDetection.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attr']) {
      this.calcAttr = null;
      if (this.attr != null && this.attr.attr.source === Attr.Source.Calc) {
        this.calcAttr = this.attr.attr;
      }
      this.hasContextMenu = !this.attr.isSystem;

      this.canModifyAttr = this.attr.isColumn || this.isLocalCalcField();
      this.hideFormatMenuOption = [DataType.Name.Boolean, DataType.Name.String].some(
        (type) => this.attr.dataType === type
      );

      const field = this.attr.withNewFormula(this.attr.formula);
      this.defaultAgg = field.defaultAggregate;
    }
  }

  isLocalCalcField(): boolean {
    return this.attr.isCalc && this.attr.attr.isLocal === true;
  }

  isCalcFieldWithError(): boolean {
    return this.attr.isCalc && (this.hasLocalCalcNameError || this.hasParentCalcNameError || !this.attr.isValid);
  }

  isExploreish() {
    return this.readOnly || this.chartObjectType === ChartTypes.ObjectType.Exploration;
  }

  isDiscrete(): boolean {
    return this.attr.isDiscrete;
  }

  isContinuous(): boolean {
    return this.attr.isContinuous;
  }

  getFieldSvgIcon() {
    if (this.attr == null) {
      return '';
    }

    if (this.attr.isValues) {
      return 'icon-measure-values';
    }
    if (this.attr.isNames) {
      return 'icon-measure-names';
    }
    if (this.attr.isCalc && this.isCalcFieldWithError()) {
      return 'icon-field-calc-broken';
    }

    let icon = getFieldIcon(this.attr.dataType, this.attr.isCalc);
    if (!icon) {
      icon = this.attr.isCalc ? 'icon-field-calc-string' : 'icon-field-string';
    }
    return icon;
  }

  get errorHtml() {
    if ((this.hasLocalCalcNameError || this.hasParentCalcNameError) && !this.attr.isValid) {
      return genericErrorHTML;
    } else if (this.hasLocalCalcNameError) {
      return localNameErrorHtml;
    } else if (this.hasParentCalcNameError) {
      return parentNameErrorHtml;
    }
    return formulaErrorHtml;
  }

  get attrName() {
    return this.attr != null && this.attr.attrName;
  }

  get dataTestName() {
    return this.attr != null ? `field-list-attr-${this.attr.attrName}` : '';
  }

  get attrTooltipHtml() {
    const iconData = this.getToolTipIcon();
    const description = this.attr.attr.desc?.replace('<p><br></p>', '<p class="line-break"></p>');
    const hasDescription = description && description.length > 0;
    const attrName = this.attr.attrName ?? '';
    if (this.attr == null) {
      return '';
    }
    if (this.attr.isNames) {
      return this.makeSystemToolTip(
        iconData,
        attrName,
        'A Mode-generated discrete field that includes data from all measures'
      );
    } else if (this.attr.isValues) {
      return this.makeSystemToolTip(
        iconData,
        attrName,
        'A Mode-generated continuous field that includes data from all measures'
      );
    } else if (!this.attr.isSystem && hasDescription) {
      return this.makeFieldsToolTip(iconData, attrName, this.sanitizer.sanitize(1, description));
    }
    return this.makeAttrNameTooltip(iconData, attrName);
  }

  get isMeasure() {
    return !this.isDimension;
  }

  get isDimension() {
    if (this.attr.isColumn) {
      return Switch.Attrs.detectDimension(this.attr.attr.name, this.attr.attr.formula.type.name);
    }

    return !this.attr.isFormulaNumeric;
  }

  getToolTipIcon(): string {
    if (this.attr.isNames) return capraIconMeasureNames.data;
    if (this.attr.isValues) return capraIconMeasureValues.data;
    if (this.attr.isCalc) return capraIconFieldCalcNumeric.data;
    return iconDataFromColumnType(this.attr.dataType);
  }

  get attrTooltipSize(): TooltipSize {
    return this.attr?.isSystem ? 'regular' : 'regular-thin';
  }

  dragstart(ev: DragEvent, attr: CompiledAttr) {
    if (this.attr.isValid) {
      this.dragStart.emit({ $field: attr, $source: 'field-list', $valid: true });
    } else {
      ev.preventDefault();
    }
  }

  dragend() {
    this.dragEnd.emit();
  }

  editCalc() {
    if (this.calcAttr) {
      this.editCalcField.emit(this.calcAttr);
    }
  }

  viewCalc() {
    if (this.calcAttr) {
      this.viewCalcField.emit(this.calcAttr);
    }
  }

  removeCalc() {
    if (this.calcAttr) {
      this.removeCalcField.emit(this.calcAttr);
    }
  }

  duplicateCalc() {
    if (this.calcAttr) {
      this.duplicateCalcField.emit(this.calcAttr);
    }
  }

  onContextMenu($event: MouseEvent, attr: CompiledAttr): void {
    this.contextMenuService.show.next({
      // Optional - if unspecified, all context menu components will open
      contextMenu: this.menu,
      event: $event,
      item: attr,
    });
    $event.preventDefault();
    $event.stopPropagation();
  }

  openColorModal() {
    let orgPalettes: OrgTypes.ColorPalette[] = [];

    // gather available org palettes to pass into modal
    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$), take(1)).subscribe((palettes) => {
      orgPalettes = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
    });

    this.colorsModalFacade.openFromFieldList(
      this.attr.attr,
      this.view,
      orgPalettes,
      this.queryRunToken,
      (field, color) => {
        const attr = this.getAttrFromFormula(field.formula);
        if (attr == null) {
          throw new Error(`Could not get attr from formula: ${field.formula}`);
        }

        this.applyFieldColor.emit({
          color,
          attr,
        });
      },
      () => {
        // TODO: handle cancel
      }
    );
  }

  private getAttrFromFormula(formula: string): FlamingoTypes.ModeAttr | undefined {
    const { attrName } = this.attr.withNewFormula(formula);
    if (attrName?.length) {
      const model = Switch.Models.make('tmp', [this.view]);
      return Switch.Attrs.find(model, this.view.name, attrName);
    }
  }

  openFormatModal() {
    this.formatModalService.open({
      title: this.attr.attrName!,
      colType: this.attr.dataType!,
      format: this.attr.defaultFormat,
      defaultFormatType: true,
      applyCustomFormat: ({ textFormat }) => {
        this.applyFieldFormat.emit({
          attr: this.attr.attr,
          format: textFormat,
        });
      },
    });
  }

  setDefaultAggregate(aggType: ExprCall.Name) {
    this.applyDefaultAgg.emit({
      attr: this.attr.attr,
      defaultAgg: aggType,
    });
  }

  makeSystemToolTip(iconData: string, attrName: string, attrDesc: string | null) {
    return `
    <div style='display:flex;flex-direction:column;width:215px'>
      <div style='display:flex'>
        <div style='width:16px;height:16px'>${iconData}</div>
        <span style='padding-left:8px;font-weight:500'>${attrName}</span>
      </div>
      <div style='text-align:left; padding-left:24px'>${attrDesc}</div>
    </div>`;
  }

  makeFieldsToolTip(iconData: string, attrName: string, attrDesc: string | null) {
    return `
    <div style='display:flex;flex-direction:column;max-width:300px;padding:8px'>
      <div style='display:flex;flex-direction:row'>
        <div style='width:16px;height:16px'>${iconData}</div>
        <span style='padding-left:6px;font-weight:500; margin-top:-1px'>${attrName}</span>
      </div>
      <div style='text-align:left; margin-left:22px'>${attrDesc}</div>
    </div>`;
  }

  makeAttrNameTooltip(iconData: string, attrName: string) {
    return `
    <div style='display:flex;flex-direction:column;max-width:300px;padding:8px'>
      <div style='display:flex;flex-direction:row'>
        <div style='width:16px;height:16px'>${iconData}</div>
        <span style='padding-left:6px;font-weight:500;margin-top:-1px'>${attrName}</span>
      </div>
    </div>`;
  }
}
